import React from 'react';
import ReactDOM from 'react-dom/client';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { httpBatchLink } from '@trpc/client';
import superjson from 'superjson';
import { Toaster, toast } from 'sonner';
import { trpc } from './lib/trpc';
import { translateError } from './lib/errors';
import { ThemeProvider } from './contexts/ThemeContext';
import { LocaleProvider } from './contexts/LocaleContext';
import { AuthProvider } from './contexts/AuthContext';
import { App } from './App';
import './index.css';

function isUnauthorized(err: unknown): boolean {
  return (
    !!err &&
    typeof err === 'object' &&
    'data' in err &&
    (err as { data?: { code?: string } }).data?.code === 'UNAUTHORIZED'
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false, staleTime: 15_000 },
  },
  mutationCache: new MutationCache({
    onError(err, _vars, _ctx, mutation) {
      if (isUnauthorized(err)) return;
      // Skip if the mutation supplies its own onError handler — it's owning the UX.
      if (mutation.options.onError) return;
      toast.error(translateError(err));
    },
  }),
});

// Redirect to /login on any UNAUTHORIZED error (mirrors Targenix behaviour).
queryClient.getQueryCache().subscribe((e) => {
  if (e.type === 'updated' && e.action.type === 'error' && isUnauthorized(e.query.state.error)) {
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login'))
      window.location.href = '/login';
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: '/api/trpc',
      transformer: superjson,
      fetch(input, init) {
        return fetch(input, { ...(init ?? {}), credentials: 'include' });
      },
    }),
  ],
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <LocaleProvider>
            <AuthProvider>
              <App />
              <Toaster richColors closeButton position="top-right" />
            </AuthProvider>
          </LocaleProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </trpc.Provider>
  </React.StrictMode>,
);
